import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Box, 
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useTheme,
  Badge,
  Divider,
  Chip,
  Stack,
  alpha
} from '@mui/material';
import { 
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Notifications as NotificationsIcon,
  DoneAll,
  Settings
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import { useAuth } from '../../../context/AuthContext';
import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import notificationApi from '../../../services/notificationApi';
import { useNavigate } from 'react-router-dom';

export default function AdminHeader({ handleDrawerToggle, isMobile }) {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const open = Boolean(anchorEl);
  const openNotif = Boolean(notifAnchorEl);
  const theme = useTheme();

  const unreadCount = notifications.filter(n => !n.isRead).length;

  useEffect(() => {
    let socket;
    let retryCount = 0;
    const maxRetries = 3;
  
    const connectSocket = () => {
      socket = io(process.env.REACT_APP_IMAGE_BASE_URL, {
        withCredentials: true,
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        autoConnect: true,
        forceNew: true
      });
    
      socket.on('connect', () => {
        console.log('✅ WebSocket connected with ID:', socket.id);
      });
  
      socket.on('connect_error', (err) => {
        console.error('❌ Connection error:', err.message);
        if (retryCount < maxRetries) {
          retryCount++;
          const delay = Math.min(1000 * Math.pow(2, retryCount), 30000);
          console.log(`Retrying in ${delay}ms... (Attempt ${retryCount}/${maxRetries})`);
          setTimeout(connectSocket, delay);
        } else {
          console.error('Max retry attempts reached');
        }
      });
  
      socket.on('disconnect', (reason) => {
        console.log('⚠️ Disconnected:', reason);
        if (reason === 'io server disconnect') {
          setTimeout(connectSocket, 1000);
        }
      });
  
      socket.on('new-notification', (newNotification) => {
        console.log('New notification received:', newNotification);
        setNotifications(prev => [newNotification, ...prev]);
      });
    };
  
    const fetchNotifications = async () => {
      try {
        const response = await notificationApi.getAllNotication();
        setNotifications(response.data);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchNotifications();
    connectSocket();
  
    return () => {
      if (socket) {
        socket.off('connect');
        socket.off('connect_error');
        socket.off('disconnect');
        socket.off('new-notification');
        socket.disconnect();
      }
    };
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotifClick = (event) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleClose();
    setOpenDialog(true);
  };

  const handleConfirmLogout = () => {
    setOpenDialog(false);
    logout();
  };

  const handleCancelLogout = () => {
    setOpenDialog(false);
  };

  const handleNotificationClick = (notification) => {
    if (notification.type === 'contact') {
      navigate('/admin/contacts');
    } else if (notification.type === 'order') {
      navigate('/admin/orders');
    }
    
    handleNotifClose();
  };

  const handleMarkAllAsRead = async () => {
    const unreadIds = notifications
      .filter(n => !n.isRead)
      .map(n => n.id);
    
    if (unreadIds.length > 0) {
      try {
        await notificationApi.markAsRead(unreadIds);
        setNotifications(notifications.map(n => 
          unreadIds.includes(n.id) ? { ...n, isRead: true } : n
        ));
      } catch (error) {
        console.error('Error marking notifications as read:', error);
      }
    }
  };

  const formatNotificationMessage = (notification) => {
    if (notification.notificationType === 'contact') {
      return `New contact from ${notification.contact.fullName}`;
    } else if (notification.notificationType === 'order') {
      return `New order #${notification.order.id} for ${notification.order.product.prodName}`;
    }
    return notification.message;
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: { 
            xs: '100%', 
            sm: `calc(100% - ${drawerWidth}px)` 
          },
          ml: { 
            xs: 0, 
            sm: `${drawerWidth}px` 
          },
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.05)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          zIndex: theme.zIndex.drawer + 1,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          backdropFilter: 'blur(8px)',
          backgroundImage: 'none'
        }}
      >
        <Toolbar sx={{ 
          justifyContent: 'space-between',
          minHeight: { xs: '56px', sm: '64px' },
          paddingX: { xs: 1.5, sm: 2, md: 3 }
        }}>
          <Box display="flex" alignItems="center">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: { xs: 1, sm: 2 },
                display: { sm: 'none' },
                color: theme.palette.text.primary,
                ...(isMobile && {
                  position: 'fixed',
                  left: 12,
                  zIndex: theme.zIndex.drawer + 2
                })
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                color: theme.palette.text.primary,
                ml: { xs: 0, sm: 0.5 },
                display: { xs: 'none', sm: 'block' },
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Koufuku Admin
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1.5}>
            {/* Notifications */}
            <IconButton
              onClick={handleNotifClick}
              size="medium"
              aria-controls={openNotif ? 'notifications-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openNotif ? 'true' : undefined}
              sx={{ 
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              <Badge 
                badgeContent={unreadCount} 
                color="error"
                overlap="circular"
                sx={{
                  '& .MuiBadge-badge': {
                    right: 4,
                    top: 4,
                    border: `2px solid ${theme.palette.background.paper}`,
                    padding: '0 4px',
                    fontSize: '0.65rem',
                    height: '18px',
                    minWidth: '18px'
                  }
                }}
              >
                <NotificationsIcon sx={{ 
                  fontSize: { xs: '24px', sm: '26px' },
                  color: theme.palette.text.secondary
                }} />
              </Badge>
            </IconButton>
            
            <Menu
              anchorEl={notifAnchorEl}
              id="notifications-menu"
              open={openNotif}
              onClose={handleNotifClose}
              PaperProps={{
                elevation: 4,
                sx: {
                  width: { xs: '320px', sm: '400px' },
                  maxHeight: '500px',
                  overflow: 'auto',
                  mt: 1.5,
                  borderRadius: '12px',
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: theme.shadows[16],
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                  },
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center" 
                px={2.5} 
                py={1.5}
                sx={{
                  backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.8) : alpha(theme.palette.primary.light, 0.1),
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Notifications
                </Typography>
                {unreadCount > 0 && (
                  <Chip 
                    label={`${unreadCount} new`}
                    size="small"
                    color="primary"
                    sx={{
                      fontWeight: 600,
                      borderRadius: '8px'
                    }}
                  />
                )}
              </Stack>
              
              {unreadCount > 0 && (
                <MenuItem 
                  onClick={handleMarkAllAsRead}
                  sx={{ 
                    justifyContent: 'center',
                    py: 1,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    }
                  }}
                >
                  <DoneAll fontSize="small" sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="body2" color={theme.palette.primary.main}>
                    Mark all as read
                  </Typography>
                </MenuItem>
              )}
              
              {loading ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading notifications...
                  </Typography>
                </Box>
              ) : notifications.length > 0 ? (
                notifications.map((notification) => (
                  <MenuItem 
                    key={notification.id} 
                    onClick={() => handleNotificationClick(notification)}
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      backgroundColor: notification.isRead ? 'inherit' : alpha(theme.palette.primary.light, 0.08),
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: notification.isRead ? 'normal' : 600,
                            flexGrow: 1,
                            color: notification.isRead ? 'text.secondary' : 'text.primary'
                          }}
                        >
                          {formatNotificationMessage(notification)}
                        </Typography>
                        {!notification.isRead && (
                          <Box component="span" sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                            ml: 1,
                            mt: 0.5
                          }} />
                        )}
                      </Stack>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          display: 'block', 
                          mt: 0.5,
                          fontSize: '0.75rem'
                        }}
                      >
                        {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              ) : (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No notifications available
                  </Typography>
                </Box>
              )}
            </Menu>

            {/* User Profile */}
            <IconButton
              onClick={handleClick}
              size="medium"
              aria-controls={open ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              sx={{
                p: 0.5,
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              {currentUser?.avatar ? (
                <Avatar 
                  src={currentUser.avatar} 
                  sx={{ 
                    width: { xs: 34, sm: 38 }, 
                    height: { xs: 34, sm: 38 },
                    backgroundColor: theme.palette.primary.light
                  }} 
                />
              ) : (
                <Avatar 
                  sx={{ 
                    width: { xs: 34, sm: 38 }, 
                    height: { xs: 34, sm: 38 },
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.contrastText
                  }}
                >
                  {currentUser?.name?.charAt(0)?.toUpperCase() || 'A'}
                </Avatar>
              )}
            </IconButton>
            
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              PaperProps={{
                elevation: 4,
                sx: {
                  overflow: 'visible',
                  mt: 1.5,
                  minWidth: '240px',
                  borderRadius: '12px',
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: theme.shadows[16],
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                  },
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem sx={{ py: 1.5, px: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  {currentUser?.avatar ? (
                    <Avatar 
                      src={currentUser.avatar} 
                      sx={{ 
                        width: 40, 
                        height: 40,
                        backgroundColor: theme.palette.primary.light
                      }} 
                    />
                  ) : (
                    <Avatar 
                      sx={{ 
                        width: 40, 
                        height: 40,
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.primary.contrastText
                      }}
                    >
                      {currentUser?.name?.charAt(0)?.toUpperCase() || 'A'}
                    </Avatar>
                  )}
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {currentUser?.name || 'Admin User'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {currentUser?.email || 'admin@example.com'}
                    </Typography>
                  </Box>
                </Stack>
              </MenuItem>
              
              <Divider sx={{ my: 0.5 }} />
              
              <MenuItem 
                onClick={handleLogoutClick}
                sx={{ 
                  py: 1.5, 
                  px: 2,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.08)
                  }
                }}
              >
                <ListItemIcon>
                  <Logout fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="Logout" 
                  primaryTypographyProps={{ 
                    color: 'error',
                    fontWeight: 500
                  }} 
                />
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCancelLogout}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullScreen={isMobile}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ 
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.8) : alpha(theme.palette.primary.light, 0.1)
        }}>
          <Typography variant="h6" fontWeight="bold">
            Confirm Logout
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Logout color="error" sx={{ fontSize: '48px' }} />
          </Box>
          <DialogContentText 
            id="alert-dialog-description" 
            sx={{ 
              textAlign: 'center',
              color: theme.palette.text.primary,
              fontSize: '1rem'
            }}
          >
            Are you sure you want to logout from the admin panel?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{
          px: 3,
          py: 2,
          justifyContent: 'center',
          gap: 2 
        }}>
          <Button 
            onClick={handleCancelLogout} 
            variant="outlined"
            size={isMobile ? 'large' : 'medium'}
            sx={{ 
              minWidth: 120,
              borderRadius: '8px',
              textTransform: 'none',
              borderWidth: '2px',
              '&:hover': {
                borderWidth: '2px'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmLogout} 
            color="error"
            variant="contained"
            size={isMobile ? 'large' : 'medium'}
            sx={{ 
              minWidth: 120,
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: theme.palette.error.dark
              }
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const drawerWidth = 260;